import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { GET_USER_ACCOUNTS } from '../graphql';
import { GetUserAccountsResponse } from '../types/graphql';
import { useAuth } from './AuthContext';

export type UserAccount = GetUserAccountsResponse['userAccounts'][number];

const SELECTED_ACCOUNT_STORAGE_KEY = 'selected_account_id';

function pickDefaultAccount(accounts: UserAccount[]): UserAccount | null {
  if (accounts.length === 0) return null;

  const storedId = localStorage.getItem(SELECTED_ACCOUNT_STORAGE_KEY);
  if (storedId) {
    const stored = accounts.find((account) => account.id === storedId);
    if (stored) return stored;
  }

  // Stable fallback: oldest membership first (matches prior page init behavior).
  return [...accounts].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )[0];
}

interface AccountContextType {
  accounts: UserAccount[];
  selectedAccount: UserAccount | null;
  setSelectedAccount: (account: UserAccount | null) => void;
  selectAccountById: (accountId: string) => void;
  accountId: string | undefined;
  isLoading: boolean;
  error?: ApolloError;
  refetchAccounts: () => Promise<unknown>;
}

const AccountContext = createContext<AccountContextType | null>(null);

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [selectedAccount, setSelectedAccountState] = useState<UserAccount | null>(null);

  const { data, loading, error, refetch } = useQuery<GetUserAccountsResponse>(GET_USER_ACCOUNTS, {
    skip: !isAuthenticated,
  });

  const accounts = useMemo(() => data?.userAccounts ?? [], [data?.userAccounts]);

  // Resolve / repair selection whenever memberships load or change.
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedAccountState(null);
      return;
    }
    if (loading) return;

    if (accounts.length === 0) {
      setSelectedAccountState(null);
      localStorage.removeItem(SELECTED_ACCOUNT_STORAGE_KEY);
      return;
    }

    setSelectedAccountState((current) => {
      if (current && accounts.some((account) => account.id === current.id)) {
        // Keep the selection object fresh (name/plan/products may have changed).
        return accounts.find((account) => account.id === current.id) ?? current;
      }
      return pickDefaultAccount(accounts);
    });
  }, [accounts, isAuthenticated, loading]);

  const setSelectedAccount = useCallback((account: UserAccount | null) => {
    setSelectedAccountState(account);
    if (account?.id) {
      localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, account.id);
    } else {
      localStorage.removeItem(SELECTED_ACCOUNT_STORAGE_KEY);
    }
  }, []);

  const selectAccountById = useCallback(
    (accountId: string) => {
      const account = accounts.find((item) => item.id === accountId) ?? null;
      setSelectedAccount(account);
    },
    [accounts, setSelectedAccount]
  );

  const refetchAccounts = useCallback(() => refetch(), [refetch]);

  const value = useMemo(
    () => ({
      accounts,
      selectedAccount,
      setSelectedAccount,
      selectAccountById,
      accountId: selectedAccount?.id,
      isLoading: isAuthenticated && loading,
      error,
      refetchAccounts,
    }),
    [
      accounts,
      selectedAccount,
      setSelectedAccount,
      selectAccountById,
      isAuthenticated,
      loading,
      error,
      refetchAccounts,
    ]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};
